import React, { FC, useState, useRef, useEffect, useCallback } from 'react'
import classNames from 'classnames'

import { BulletIconMoon } from '@/molecules/bullet-icon-moon'
import { TabBodyProps, TabProps, WithChildren } from '@/types'

import { TabsContext } from './context'
import { useTabsContext } from './hooks'

import styles from './tabs.module.scss'

export const TabBody: FC<TabBodyProps> = ({ step, children }) => {
  const className = classNames(styles['tab-body'], styles['fade'])

  const divEl = useRef<HTMLDivElement>(null)
  const { step: cardCurrentStep } = useTabsContext()
  const isActive = useCallback(
    () => step === cardCurrentStep,
    [step, cardCurrentStep]
  )
  const getAction = useCallback(
    () => (isActive() ? 'add' : 'remove'),
    [isActive]
  )

  const toggleClass = (action: 'add' | 'remove', className: string) => {
    const force = action === 'add'
    return divEl.current?.classList.toggle(className, force)
  }

  useEffect(() => {
    const action = getAction()
    setTimeout(() => toggleClass(action, styles['active']), 300)
    setTimeout(() => toggleClass(action, styles['show']), 0)
  }, [getAction])

  return (
    <div ref={divEl} className={className}>
      {children}
    </div>
  )
}

export const TabBodyList: FC<WithChildren> = ({ children }) => {
  return <div>{children}</div>
}

export const Tab: FC<TabProps> = ({
  step,
  showIcon = false,
  iconName = 'check_circle',
  children,
}) => {
  const { step: cardCurrentStep, setStep } = useTabsContext()
  const className = classNames(styles['tab'], {
    [styles['active']]: step === cardCurrentStep,
  })
  return (
    <li className={className} onClick={() => setStep(step)}>
      {showIcon ? (
        <BulletIconMoon iconName={iconName} iconPosition={'end'} iconSize="md">
          {children}
        </BulletIconMoon>
      ) : (
        children
      )}
    </li>
  )
}

export const TabList: FC<WithChildren> = ({ children }) => {
  return <ul className={styles['tab-list']}>{children}</ul>
}

export const TabsProvider: FC<WithChildren> = ({ children }) => {
  const [step, setStep] = useState(1)

  return (
    <TabsContext.Provider value={{ step, setStep }}>
      <div>{children}</div>
    </TabsContext.Provider>
  )
}
